import React from 'react';
import { Tooltip as AntToolTip, Typography } from 'antd';
import { BoldLabel } from '../../components';
import {
  Flag,
  BlueLabel,
  Credit,
  Debit,
  FlexWrapper,
  RiskLevelBadge,
  BeneficiaryCell,
  BeneficiarySummaryText,
  BeneficiaryDetailsLink
} from './styled';

import { floor, truncate } from '../../../../util/lodashlike';
import { HIGH_RISK_VALUE } from './constants';
import Tooltip from '../../components/Tooltip';
import {
  convertISOToDateFormat,
  convertISOToDateTimeFormat
} from '../../../../util/timezone';

const display = (str) => (str ? str : '-');

// Null-guard for numeric amount columns: floor(undefined/null) -> NaN, so
// render the placeholder '-' instead (real data can legitimately be null).
const displayAmount = (value) =>
  value === null || value === undefined || value === ''
    ? '-'
    : floor(value, 2);

export default {
  defaultProperties: {
    ellipsis: true,
    render: (value) => (
      <div>
        <Tooltip content={value}>{truncate(value, { length: 15 })}</Tooltip>
      </div>
    )
  },
  customProperties: {
    accountId: {
      render: (_, record) =>
        record.accountId ? (
          <Typography.Text copyable={{ text: record.accountId }}>
            {record.accountId}
          </Typography.Text>
        ) : (
          <>-</>
        )
    },
    transactionDate: {
      render: (_, record) => (
        <>{convertISOToDateTimeFormat(record.transactionDate)}</>
      )
    },
    // FIN-13738 — ActivityInformation falls back to this namespace, and without an entry the
    // Activity Timestamp hit defaultProperties' 15-char truncate ("2026-09-25T1...").
    activityDatetime: {
      render: (_, record) => (
        <>{convertISOToDateTimeFormat(record.activityDatetime) || '-'}</>
      )
    },
    isCredit: {
      render: (_, record) => {
        return (
          <FlexWrapper>
            {record.isCredit == undefined || record.isCredit == null ? (
              <>-</>
            ) : record.isCredit ? (
              <>
                <Credit /> CR
              </>
            ) : (
              <>
                <Debit /> DR
              </>
            )}
          </FlexWrapper>
        );
      }
    },
    amount: {
      render: (_, record) => (
        <AntToolTip title={record.amount} placement="topLeft">
          <BoldLabel>{displayAmount(record.amount)}</BoldLabel>
        </AntToolTip>
      )
    },
    transactionCurrencyAmount: {
      render: (_, record) => (
        <AntToolTip
          title={record.transactionCurrencyAmount}
          placement="topLeft">
          <BoldLabel>
            {displayAmount(record.transactionCurrencyAmount)}
          </BoldLabel>
        </AntToolTip>
      )
    },
    localCurrencyEquivalent: {
      render: (_, record) => (
        <AntToolTip title={record.localCurrencyEquivalent} placement="topLeft">
          <BoldLabel>{displayAmount(record.localCurrencyEquivalent)}</BoldLabel>
        </AntToolTip>
      )
    },
    oppAmountUsd: {
      render: (_, record) => (
        <AntToolTip title={record.oppAmountUsd} placement="topLeft">
          <BoldLabel>{displayAmount(record.oppAmountUsd)}</BoldLabel>
        </AntToolTip>
      )
    },
    countryOfTransaction: {
      render: (value, record) => (
        <Flag
          country={record.countryOfTransaction}
          showCountryName
          defaultValue={record.countryOfTransaction}
        />
      )
    },
    originatorBankCountryCode: {
      render: (value, record) => (
        <Flag
          country={record.originatorBankCountryCode}
          showCountryName
          defaultValue={record.originatorBankCountryCode}
        />
      )
    },
    beneficiaryBankCountryCode: {
      render: (value, record) => (
        <Flag
          country={record.beneficiaryBankCountryCode}
          showCountryName
          defaultValue={record.beneficiaryBankCountryCode}
        />
      )
    },
    transactionTypeCode: {
      render: (_, record) => <BlueLabel>{record.transactionTypeCode}</BlueLabel>
    },
    sourceOfTxnFunds: {
      // FIN-12419 — stack value above the High Risk badge (inline column, no new
      // styled-component so styled-components' global componentIds — and thus
      // class-name snapshots elsewhere — are unaffected) so the value keeps full
      // width and word-wraps instead of collapsing to one char per line when the
      // badge is present.
      render: (value, record) => (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '2px'
          }}>
          <div>{record.sourceOfTxnFunds}</div>
          {record.sourceOfFundsRiskLevel == HIGH_RISK_VALUE && (
            <RiskLevelBadge>High Risk</RiskLevelBadge>
          )}
        </div>
      )
    },
    destinationOfFunds: {
      // FIN-12419 — see sourceOfTxnFunds above: stack value + badge inline.
      render: (value, record) => (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '2px'
          }}>
          <div>{record.destinationOfFunds}</div>
          {record.destinationOfFundsRiskLevel == HIGH_RISK_VALUE && (
            <RiskLevelBadge>High Risk</RiskLevelBadge>
          )}
        </div>
      )
    },
    originatorBeneficiaryName: {
      ellipsis: true,
      render: (value, record) => (
        <div>
          <Tooltip
            content={`${record.originatorBankName || ''} - ${
              record.beneficialBankName || ''
            }`}>
            {truncate(
              `${record.originatorBankName || ''} - ${
                record.beneficialBankName || ''
              }`,
              { length: 15 }
            )}
          </Tooltip>
        </div>
      )
    },
    beneficiarySummary: {
      render: (value, record) => {
        // FIN-11542 closing sweep: build the summary from only the parts that
        // have data, so empty fields no longer leave orphan "-, -" fragments
        // that wrap awkwardly and split the "More Details" link. Stack the
        // summary text above the link (BeneficiaryCell = column flex).
        const summaryParts = [
          record.beneficiary,
          record.beneficiaryCustomerType,
          record.beneficiaryBankCountryCode
        ].filter((p) => p !== null && p !== undefined && p !== '');
        const summaryText = summaryParts.length ? summaryParts.join(', ') : '-';
        return (
          <BeneficiaryCell>
            <BeneficiarySummaryText>{summaryText}</BeneficiarySummaryText>
            <Tooltip
              content={
                <>
                  <div>
                    {`C2C linkage present: `}
                    <strong>{`${
                      record.beneficiaryC2cLinkagePresent ? 'Yes' : 'No'
                    }`}</strong>
                  </div>
                  <div>
                    {`Adverse news on beneficiary: `}
                    <strong>{`${
                      record.beneficiaryAdverseNews ? 'Yes' : 'No'
                    }`}</strong>
                  </div>
                </>
              }>
              <BeneficiaryDetailsLink>More Details</BeneficiaryDetailsLink>
            </Tooltip>
          </BeneficiaryCell>
        );
      }
    },
    bankBranchCode: {
      ellipsis: true,
      render: (_, record) => (
        <div>
          <Tooltip content={`${record.bankBranchCode || '-'}`}>
            {truncate(`${record.bankBranchCode || '-'}`)}
          </Tooltip>
        </div>
      )
    },
    transactionTypeDescription: {
      ellipsis: true,
      render: (_, record) => (
        <div>
          <Tooltip content={`${record.transactionTypeDescription || '-'}`}>
            {truncate(`${record.transactionTypeDescription || '-'}`)}
          </Tooltip>
        </div>
      )
    },
    partyKey: {
      render: (value, record) => <>{record.partyKey || '-'}</>
    },
    counterpartyKey: {
      render: (value, record) => <>{record.counterpartyKey || '-'}</>
    },
    sourceOfTxnFunds: {
      render: (value, record) => <>{record.sourceOfTxnFunds || '-'}</>
    },
    transactionPurpose: {
      ellipsis: true,
      render: (_, record) => (
        <div>
          <Tooltip content={`${record.transactionPurpose || '-'}`}>
            {truncate(`${record.transactionPurpose || '-'}`)}
          </Tooltip>
        </div>
      )
    },
    origBankBenName: {
      ellipsis: true,
      render: (_, record) => (
        <div>
          <Tooltip content={`${record.origBankBenName || '-'}`}>
            {truncate(`${record.origBankBenName || '-'}`)}
          </Tooltip>
        </div>
      )
    },
    atmId: {
      render: (value, record) => <>{record.atmId || '-'}</>
    },
    counterpartyNationality: {
      render: (value, record) => <>{record.counterpartyNationality || '-'}</>
    },
    counterpartyType: {
      render: (value, record) => <>{record.counterpartyType || '-'}</>
    },
    thirdPartyRefund: {
      render: (value, record) => <>{record.thirdPartyRefund || '-'}</>
    },
    oppAccountNumber: {
      render: (value, record) => <>{record.oppAccountNumber || '-'}</>
    },
    bankBranchCode: {
      ellipsis: true,
      render: (_, record) => (
        <div>
          <Tooltip content={`${record.bankBranchCode || '-'}`}>
            {truncate(`${record.bankBranchCode || '-'}`)}
          </Tooltip>
        </div>
      )
    },
    counterpartyId: {
      render: (value, record) => <>{record.counterpartyId || '-'}</>
    },
    counterpartyName: {
      ellipsis: true,
      render: (_, record) => (
        <div>
          <Tooltip content={`${record.counterpartyName || '-'}`}>
            {truncate(`${record.counterpartyName || '-'}`)}
          </Tooltip>
        </div>
      )
    }
  }
};
